import React, { useRef, useId, useMemo } from "react";
import type { MutableRefObject } from "react";

import styles from "./Card.module.css";

type CardContextValue = {
  titleProps: {
    ref: MutableRefObject<HTMLHeadingElement | null>;
    id: string;
  };
  href?: string;
};

const CardContext = React.createContext<CardContextValue | null>(null);

export function useCard(href?: string) {
  const labelRef = useRef<HTMLHeadingElement | null>(null);
  const labelId = `card-title${useId()}`;
  return useMemo(
    () => ({
      titleProps: {
        ref: labelRef,
        id: labelId,
      },
      href,
    }),
    [labelId, href]
  );
}

export function useCardContext() {
  const value = React.useContext(CardContext);
  if (!value) {
    throw new Error(
      "useCardContext was called outside of a Card.Root element. No context value is available."
    );
  }
  return value;
}

export type CardRootProps = {
  id: string;
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

export const Root = React.forwardRef<HTMLElement, CardRootProps>(
  ({ className = "", href, children, as = "li", ...props }, ref) => {
    const value = useCard(href);
    const Component = as;

    return (
      <Component
        className={`${styles.root} ${className}`}
        aria-labelledby={value.titleProps.id}
        data-is-link={Boolean(href)}
        {...props}
        ref={ref}
      >
        <CardContext.Provider value={value}>{children}</CardContext.Provider>
      </Component>
    );
  }
);

Root.displayName = "Card.Root";

export type CardMediaProps = {
  palette?: string;
} & JSX.IntrinsicElements["div"];

export const Media = ({
  className = "",
  palette = "kraft-paper",
  children,
  ...props
}: CardMediaProps) => {
  return (
    <div
      className={`${styles.media} ${className}`}
      data-palette={palette}
      {...props}
    >
      {children}
    </div>
  );
};

Media.displayName = "Card.Media";

export type CardTitleProps = JSX.IntrinsicElements["h3"];

export const Title = ({
  className = "",
  children,
  ...props
}: CardTitleProps) => {
  const { titleProps, href } = useCardContext();
  const content = href ? (
    <a href={href} className={styles.link}>
      {children}
    </a>
  ) : (
    children
  );
  return (
    <h3 className={`${styles.title} ${className}`} {...titleProps} {...props}>
      {content}
    </h3>
  );
};

export type CardMetaProps = JSX.IntrinsicElements["p"];

export const Meta = ({ className = "", children, ...props }: CardMetaProps) => {
  return (
    <p className={`${styles.meta} ${className}`} {...props}>
      {children}
    </p>
  );
};

Meta.displayName = "Card.Meta";

export type CardContentProps = JSX.IntrinsicElements["div"];

export const Content = ({ className = "", ...props }: CardContentProps) => {
  return <div className={`${styles.content} ${className}`} {...props} />;
};

Content.displayName = "Card.Content";

export type CardLinkProps = JSX.IntrinsicElements["a"];

export const Link = ({ className = "", href }: CardLinkProps) => {
  return <a href={href} className={`${styles.content} ${className}`}></a>;
};

Content.displayName = "Card.Link";

export type CardFooterProps = JSX.IntrinsicElements["div"];

export const Footer = ({
  className = "",
  children,
  ...props
}: CardFooterProps) => {
  return (
    <footer className={`${styles.footer} ${className}`} {...props}>
      {children}
    </footer>
  );
};

Footer.displayName = "Card.Footer";
